import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { filter, propEq } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Filter, FiltersPropsShared } from '../../Filters.types';
import { Domain } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';

import { DELETE_FILTER_MUTATION } from './DeleteFilter.gql';
import { LOAD_FILTERS_QUERY } from './LoadFilters.gql';
import { SAVE_FILTER_MUTATION } from './SaveFilter.gql';
import { UPDATE_FILTER_MUTATION } from './UpdateFilter.gql';

const defaultFilters: Filter[] = [];

export enum FilterSaveStatus {
  DISABLED,
  UNSAVED,
  SAVED,
}

export interface UseFiltersProps {
  domain?: Domain;
  queryId?: string;
}

export interface UseFiltersResult extends FiltersPropsShared {
  filtersByQueryId: Record<string, Filter>;
  saveStatus: FilterSaveStatus;
  saving: boolean;
  error?: boolean;
  onFilterSave: (name: string) => void;
}

export const useFilters = ({ domain, queryId }: UseFiltersProps): UseFiltersResult => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Base.common' });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const track = useTrackInteraction();
  const { filters: filterConfig } = useConfig();
  const { data: queryData, loading: queryLoading, error: queryError } = useQuery(LOAD_FILTERS_QUERY);

  const [save] = useMutation(SAVE_FILTER_MUTATION, { onError });
  const [remove] = useMutation(DELETE_FILTER_MUTATION, { onError });
  const [update] = useMutation(UPDATE_FILTER_MUTATION, { onError });

  const filters = (queryData?.filters || defaultFilters) as Filter[];

  const filterDelete = useCallback((filter: Filter) => {
    remove({
      variables: {
        id: filter.id,
      },
      refetchQueries: [{ query: LOAD_FILTERS_QUERY }],
    });
    track(Target.FILTER, Action.DELETED, { filterId: filter.id, queryId, domain });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const filterToggleFavorites = useCallback(
    (value: Partial<Filter>) => {
      update({
        variables: {
          id: value.id,
          isFavorite: !value.isFavorite,
        },
        refetchQueries: [{ query: LOAD_FILTERS_QUERY }],
      });
      track(Target.FILTER_FAVORITES, Action.TOGGLED, { filterId: value.id, queryId, domain });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const filtersByDomain = useMemo(() => {
    return domain ? filter(propEq(domain, 'domain'), filters) : filters;
  }, [filters, domain]);

  const filtersByQueryId = useMemo(() => {
    return filtersByDomain.reduce(
      (acc, filter) => {
        const key = filter.queryId;
        // if a filter with the same queryId already exists in the result check if it's the default filter
        // the user filter takes precedence in this case
        if (!acc[key] || (acc[key] && !acc[key].userId)) {
          acc[key] = filter;
        }
        return acc;
      },
      {} as Record<string, Filter>,
    );
  }, [filtersByDomain]);

  const favoriteFilters = useMemo(() => {
    // if filter is not in filtersByQueryId, it means there is a default filter and a user filter with the same queryId
    // the user filter takes precedence in this case
    return filter((f) => f.isFavorite === true && f === filtersByQueryId[f.queryId], filtersByDomain);
  }, [filtersByDomain, filtersByQueryId]);

  const favoriteFiltersMaxReached = favoriteFilters.length >= filterConfig.count;

  const selected = (queryId && filtersByQueryId[queryId]) || undefined;
  const filterSave = useCallback(
    async (name: string) => {
      try {
        // if selected filter has a user (=not a default filter) add the id to upsert existing filter
        const id = (selected?.userId && selected?.id) || uuidv4();
        const { errors } = await save({
          variables: {
            id,
            name,
            domain: domain,
            queryId: queryId,
            isFavorite: selected?.isFavorite || false,
          },
          refetchQueries: [{ query: LOAD_FILTERS_QUERY }],
        });
        track(Target.FILTER, Action.SAVED, { queryId, domain, filterId: id });
        if (!errors) {
          enqueueSuccessSnackbar(t('filterSaveSuccess'));
        }
      } catch (e) {
        onError(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, domain, queryId, t],
  );

  const saveStatus: FilterSaveStatus = !queryId
    ? FilterSaveStatus.DISABLED
    : selected
      ? FilterSaveStatus.SAVED
      : FilterSaveStatus.UNSAVED;

  return {
    selected,
    saveStatus,
    filters: filtersByDomain,
    favoriteFilters,
    favoriteFiltersMaxReached,
    filtersByQueryId,
    saving: queryLoading,
    error: !!queryError,
    onFilterSave: filterSave,
    onFilterDelete: filterDelete,
    onFilterToggleFavorites: filterToggleFavorites,
  };
};
